import { useEffect, useRef, useState, useCallback } from 'react'
import { X, Pin, ChevronRight, Bookmark } from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import {
  type DocHeading,
  extractHeadings,
  computeHeadingRatios,
  getActiveHeadingId,
} from '@/lib/heading-utils'
import { useSoftBookmarksEnabled } from '@/hooks/use-soft-bookmarks-enabled'
import {
  SOFT_BOOKMARK_CHANGED_EVENT,
  getSoftBookmark,
  type SoftBookmarkChangedDetail,
} from '@/lib/document-soft-bookmark'

export interface DocumentTocProps {
  /** Raw markdown string � used to keep heading ratios in sync with content. */
  markdown: string
  /** Pre-extracted headings from extractHeadings(). Ratios are updated internally. */
  headings: DocHeading[]
  /** The element containing the rendered markdown (for querying heading elements). */
  contentRef: React.RefObject<HTMLElement>
  /** The scrollable container (for scroll tracking and programmatic scrolling). */
  scrollRef: React.RefObject<HTMLElement>
  /** Called when the user closes the TOC. */
  onClose: () => void
  className?: string
  /** Document id for soft-bookmark indicator in the TOC. */
  documentId?: string | null
}

const SIZE: Record<1 | 2 | 3, string> = {
  1: 'text-xs font-semibold',
  2: 'text-xs font-medium',
  3: 'text-xs font-normal',
}

export function DocumentToc({
  headings,
  contentRef,
  scrollRef,
  onClose,
  className,
  documentId = null,
}: DocumentTocProps) {
  const [activeId, setActiveId] = useState<string | null>(headings[0]?.id ?? null)
  const [pinned, setPinned] = useState(false)
  const { enabled: bookmarksEnabled } = useSoftBookmarksEnabled()
  const [bookmarkHeadingId, setBookmarkHeadingId] = useState<string | null>(
    () => getSoftBookmark(documentId)?.headingId ?? null
  )
  const activeItemRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    setBookmarkHeadingId(getSoftBookmark(documentId)?.headingId ?? null)
  }, [documentId])

  useEffect(() => {
    if (!documentId) return
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<SoftBookmarkChangedDetail>).detail
      if (detail?.documentId !== documentId) return
      setBookmarkHeadingId(detail.bookmark?.headingId ?? null)
    }
    window.addEventListener(SOFT_BOOKMARK_CHANGED_EVENT, onChange)
    return () => window.removeEventListener(SOFT_BOOKMARK_CHANGED_EVENT, onChange)
  }, [documentId])

  // Track active heading on scroll
  useEffect(() => {
    const scrollEl = scrollRef.current
    const contentEl = contentRef.current
    if (!scrollEl || !contentEl || headings.length === 0) return

    const onScroll = () => {
      const id = getActiveHeadingId(headings, contentEl, scrollEl.scrollTop)
      setActiveId(id)
    }
    onScroll()
    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', onScroll)
  }, [headings, contentRef, scrollRef])

  // Scroll active item into view in the TOC list
  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeId])

  const scrollToHeading = useCallback(
    (id: string) => {
      const contentEl = contentRef.current
      if (!contentEl) return
      const el = contentEl.querySelector(`[id="${id}"]`) as HTMLElement | null
      if (el) {
        // scrollIntoView respects scroll-margin-top, so the pill never covers the heading.
        // Close the TOC after a short delay so the layout shift doesn't interfere with
        // the smooth-scroll animation.
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setActiveId(id)
        if (!pinned) setTimeout(() => onClose(), 80)
      }
    },
    [contentRef, pinned, onClose]
  )

  // Keyboard close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className={cn(
        'flex flex-col border-r border-border bg-sidebar overflow-hidden',
        'animate-in slide-in-from-left-2 duration-150',
        className
      )}
      style={{ width: 200, flexShrink: 0 }}
    >
      {/* Header */}
      <div className="flex items-center px-3 py-2 border-b border-border flex-shrink-0">
        <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Contents
        </span>
        <div className="flex gap-0.5">
          <button
            className={cn(
              'flex items-center justify-center w-6 h-6 rounded transition-colors',
              'text-muted-foreground hover:text-foreground hover:bg-accent',
              pinned && 'text-primary bg-primary/10'
            )}
            onClick={() => setPinned((v) => !v)}
            title={pinned ? 'Unpin (auto-close on click)' : 'Pin open'}
          >
            <Pin className="w-3.5 h-3.5" />
          </button>
          <button
            className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            onClick={onClose}
            title="Close (Esc)"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Heading list */}
      <div className="flex-1 overflow-y-auto py-1.5">
        {headings.length === 0 ? (
          <p className="px-3 py-4 text-xs text-muted-foreground">No headings found.</p>
        ) : (
          headings.map((h) => {
            const isActive = activeId === h.id
            return (
              <button
                key={h.id}
                ref={isActive ? activeItemRef : null}
                className={cn(
                  'flex items-center gap-1 w-full text-left transition-colors',
                  'py-[3px] pr-3 rounded-none border-none',
                  SIZE[h.level],
                  isActive
                    ? 'text-primary bg-primary/8'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
                style={{ paddingLeft: h.level === 1 ? 12 : h.level === 2 ? 28 : 40 }}
                onClick={() => scrollToHeading(h.id)}
              >
                {isActive && <ChevronRight className="w-2.5 h-2.5 flex-shrink-0 opacity-80" />}
                <span
                  className={cn(
                    'flex-1 overflow-hidden text-ellipsis whitespace-nowrap',
                    isActive && 'font-medium'
                  )}
                >
                  {h.text}
                </span>
                {bookmarksEnabled && bookmarkHeadingId === h.id && (
                  <Bookmark
                    className="w-3 h-3 flex-shrink-0 text-primary fill-current"
                    data-testid={testIds.documentToc.bookmarkIcon}
                    aria-label="Reading bookmark"
                  />
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Current Section Pill
// ---------------------------------------------------------------------------

export interface CurrentSectionPillProps {
  /** Headings used to track the active section label. */
  headings: DocHeading[]
  /** The element containing rendered markdown. */
  contentRef: React.RefObject<HTMLElement>
  /** The scrollable container. */
  scrollRef: React.RefObject<HTMLElement>
  /** Whether the TOC panel is currently open. */
  tocOpen: boolean
  /** Called to toggle the TOC panel. */
  onToggle: () => void
  /** Extra top offset (px) when other absolute chrome (e.g. change banner) sits above the pill. */
  topOffsetPx?: number
}

export function CurrentSectionPill({
  headings,
  contentRef,
  scrollRef,
  tocOpen,
  onToggle,
  /** Extra top offset (px) when other absolute chrome (e.g. change banner) sits above the pill. */
  topOffsetPx = 0,
}: CurrentSectionPillProps) {
  const [activeHeading, setActiveHeading] = useState<DocHeading | null>(headings[0] ?? null)

  useEffect(() => {
    const scrollEl = scrollRef.current
    const contentEl = contentRef.current
    if (!scrollEl || !contentEl || headings.length === 0) return

    const onScroll = () => {
      const id = getActiveHeadingId(headings, contentEl, scrollEl.scrollTop)
      setActiveHeading(headings.find((h) => h.id === id) ?? null)
    }
    onScroll()
    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', onScroll)
  }, [headings, contentRef, scrollRef])

  if (!activeHeading || tocOpen) return null

  // Base top-2 (8px) plus chrome above the reading column (KH strip, change banner, etc.).
  const topPx = 8 + Math.max(0, topOffsetPx)

  return (
    /* Show only when the viewport is wide enough that the pill sits in the left gutter
       rather than overlapping the 760px reading column. Keep max width under ~160px so a
       long H1 does not read as a second toolbar chip beside Request review. */
    <div
      className="hidden xl:block absolute left-4 z-10 w-[min(148px,calc(100%-2rem))] max-w-[148px] transition-[top] duration-200 pointer-events-none"
      style={{ top: topPx }}
      data-testid={testIds.documentToc.currentSectionPill}
    >
      <button
        type="button"
        onClick={onToggle}
        title="Open table of contents"
        className={cn(
          'pointer-events-auto inline-flex items-center gap-1 w-full min-w-0',
          'px-2 py-1 rounded-full border text-[11px] leading-tight',
          'transition-all duration-150 cursor-pointer',
          'shadow-sm text-left',
          tocOpen
            ? 'bg-primary/8 border-primary/30 text-primary'
            : 'bg-background/90 border-border text-muted-foreground hover:bg-accent hover:text-foreground hover:border-primary/30'
        )}
      >
        <ChevronRight
          className={cn(
            'w-3 h-3 shrink-0 opacity-60 transition-transform duration-150',
            tocOpen && 'rotate-90'
          )}
        />
        <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium">
          {activeHeading.text}
        </span>
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hook: useDocumentToc
// Manages all TOC state and heading computation for use in DocumentViewer.
// ---------------------------------------------------------------------------

export function useDocumentToc(
  markdown: string,
  contentRef: React.RefObject<HTMLElement>,
  scrollRef: React.RefObject<HTMLElement>
) {
  const [tocOpen, setTocOpen] = useState(false)
  const [headings, setHeadings] = useState<DocHeading[]>([])

  // Extract headings from the markdown (no DOM needed)
  useEffect(() => {
    setHeadings(extractHeadings(markdown).map((h) => ({ ...h, ratio: 0 })))
  }, [markdown])

  // Compute ratios once the content is in the DOM
  useEffect(() => {
    const contentEl = contentRef.current
    const scrollEl = scrollRef.current
    if (!contentEl || !scrollEl || headings.length === 0) return

    const update = () => {
      setHeadings((prev) => computeHeadingRatios(prev, contentEl, scrollEl))
    }

    // Run after paint so offsetTop values are accurate
    const raf = requestAnimationFrame(() => {
      update()
      const ro = new ResizeObserver(update)
      ro.observe(contentEl)
      return () => ro.disconnect()
    })

    return () => cancelAnimationFrame(raf)
  }, [headings.length, contentRef, scrollRef])

  return {
    tocOpen,
    setTocOpen,
    headings,
  }
}
